import React, { useMemo } from "react";
import {
  Carousel,
  ContentCard,
  ContentCardSkeleton,
  EmptyState,
} from "~/components";
import { usePassionsData, PASSION_DEFINITIONS } from "~/hooks/usePassionsData";
import { usePassionsStore } from "~/store";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";
import { useShallow } from "zustand/react/shallow";
import destinationPassionMap from "~/data/destinations.json";
import destinationDataFallback from "~/data/destinationData.json";

const DEFAULT_PASSIONS = ["beach", "golf", "ski"];
const EXPLORE_COLLECTIONS_URL =
  "https://homes-and-villas.marriott.com/en/collections";

const getActivitiesForDestination = (destination) => {
  const activities = [];
  Object.keys(PASSION_DEFINITIONS).forEach((key) => {
    // Map the passion key to the destination property key (Capitalized)
    // e.g. 'beach' -> 'Beach', 'ski' -> 'Ski'
    const destKey = key.charAt(0).toUpperCase() + key.slice(1);
    const score = destination[destKey];

    // If the destination has a high score for this passion, add it as an activity
    if (score && parseFloat(score) >= 70) {
      activities.push({
        label: PASSION_DEFINITIONS[key].label,
        icon: PASSION_DEFINITIONS[key].icon,
      });
    }
  });
  // Limit to 3 activities
  return activities.slice(0, 3);
};

export const PassionCarousel = () => {
  const { getSelectedPassions, loading, error } = usePassionsData();

  // Get selected passions from store for reactive updates (guest selections)
  // Use useShallow to properly detect array content changes
  const storeSelectedPassions = usePassionsStore(
    useShallow((state) => state.selectedPassions),
  );

  // Get the user's selected passions from profile
  const profilePassions = getSelectedPassions();

  // Use store passions if available (for guest selections), otherwise use profile passions
  const selectedPassions =
    storeSelectedPassions.length > 0 ? storeSelectedPassions : profilePassions;

  // Filter carousel data based on selected passions
  // If no passions are selected or there's an error, show default popular categories
  const passionsToShow =
    selectedPassions.length > 0 ? selectedPassions : DEFAULT_PASSIONS;

  // Memoize passion tiles to update when selected passions change
  const passionTiles = useMemo(() => {
    const tiles = [];
    const usedIds = new Set(); // To avoid duplicates if same destination in multiple lists

    const isSinglePassion = passionsToShow.length === 1;

    passionsToShow.forEach((passion) => {
      let rawDestinations = destinationPassionMap[passion];
      let isFallbackData = false;

      // If no destinations in main map, try fallback data (e.g., for 'hike')
      if (!rawDestinations || rawDestinations.length === 0) {
        rawDestinations = destinationDataFallback[passion]?.slides;
        isFallbackData = true;
      }

      if (rawDestinations && rawDestinations.length > 0) {
        //Logic: If only one passion, show 3 destinations. If multiple, show 1 for each.
        const limit = isSinglePassion ? 3 : 1;

        rawDestinations.slice(0, limit).forEach((dest) => {
          // Normalization logic
          let tile;
          if (isFallbackData) {
            tile = {
              id: `${passion}-${dest.title}`,
              title: dest.title,
              description: dest.description,
              image: dest.image,
              buttonLink: dest.buttonLink,
              activities: dest.activities,
              passionSource: passion,
            };
          } else {
            tile = {
              id: dest.city, // Use city as key
              title: dest.headline || `Explore ${dest.city}`,
              description:
                dest.description || `Discover the best of ${dest.city}`,
              image: dest.imageUrl,
              buttonLink: dest.pageUrl,
              activities: getActivitiesForDestination(dest),
              passionSource: passion,
            };
          }

          if (usedIds.has(tile.id)) return;
          usedIds.add(tile.id);
          tiles.push(tile);
        });
      }
    });

    return tiles;
  }, [passionsToShow]);

  const handleTitleClick = () => {
    window.open(EXPLORE_COLLECTIONS_URL, "_blank", "noopener,noreferrer");
  };

  const handleSlideClick = (slide) => {
    console.log("Clicked passion slide:", slide);
    // Open the button link in a new tab
    if (slide.buttonLink) {
      window.open(slide.buttonLink, "_blank", "noopener,noreferrer");
    }
  };

  // If error, log it but continue with fallback content
  if (error) {
    console.warn("Error loading passions, showing default content:", error);
  }

  // If loading, show skeleton cards
  if (loading) {
    return (
      <div style={{}}>
        <Carousel
          title="Curated Destinations for you"
          onTitleClick={handleTitleClick}
          showNavigation={true}
          gap={32}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <ContentCardSkeleton key={`skeleton-${index}`} />
          ))}
        </Carousel>
      </div>
    );
  }

  return (
    <div style={{}}>
      {/* Passion-based destination tiles - 6 tiles total in random order */}
      {passionTiles.length > 0 && (
        <Carousel
          title={"Curated destinations for you"}
          onTitleClick={handleTitleClick}
          gap={32}
          navButtonTop="220px"
        >
          {passionTiles.map((slide) => (
            <ContentCard
              key={slide.id}
              title={slide.title}
              description={slide.description}
              image={slide.image}
              activities={slide.activities}
              hotelLink={slide.buttonLink}
              onClick={() => handleSlideClick(slide)}
              style={{ width: "330px" }}
              imageStyle={{ height: "440px", width: "330px" }}
              {...getAnalyticsClickTrackingAttributes(
                "Passion Carousel",
                "click",
                `Opened ${slide.title}`,
              )}
            />
          ))}
        </Carousel>
      )}

      {/* Fallback if no carousel data is available */}
      {passionTiles.length === 0 && (
        <EmptyState
          icon="compass"
          title="No experiences yet"
          subtitle="Select your passions above to discover destinations"
        />
      )}
    </div>
  );
};
